/**
 * Shared quote / testimonial popup.
 *
 * Any button with `.js-quote-more` opens a single global modal, populated from
 * the button's data attributes:
 *   data-quote        — full quote text (required)
 *   data-quote-author — author name (optional)
 *   data-quote-role   — author role / company (optional)
 */
/**
 * Testimonial card quotes (home page `testimonials`, case-studies page
 * `cs-testimonials`): capped by CSS. Only offer the "Mehr anzeigen" modal
 * button when the quote is actually clamped.
 * Re-checked on resize, since wrapping changes.
 * (Separate from initQuoteClamp() below, which is the case-study inline toggle.)
 */
export function initTestimonialQuoteClamps() {
	const clamps = document.querySelectorAll('.js-testimonial-clamp');
	if (!clamps.length) {
		return;
	}

	const sync = () => {
		clamps.forEach((el) => {
			const button = el
				.closest('.testimonials__quote-block, .cs-testimonials__quote-block')
				?.querySelector('.js-quote-more');
			if (!button) {
				return;
			}
			// 1px tolerance for sub-pixel line heights.
			button.hidden = el.scrollHeight <= el.clientHeight + 1;
		});
	};

	sync();
	window.addEventListener('resize', sync);
	if (document.fonts?.ready) {
		document.fonts.ready.then(sync);
	}
}

/**
 * Industries cards: the body copy is capped by CSS (-webkit-line-clamp). Only
 * show the "Mehr anzeigen" button (which opens the full text in the shared
 * modal) when the copy is actually clamped. Re-checked on resize / font load.
 */
export function initIndustryQuoteClamps() {
	const bodies = document.querySelectorAll('.industries__card-body');
	if (!bodies.length) {
		return;
	}

	const sync = () => {
		bodies.forEach((el) => {
			const button = el
				.closest('.industries__card-panel')
				?.querySelector('.js-quote-more');
			if (!button) {
				return;
			}
			button.hidden = el.scrollHeight <= el.clientHeight + 1;
		});
	};

	sync();
	window.addEventListener('resize', sync);
	if (document.fonts?.ready) {
		document.fonts.ready.then(sync);
	}
}

export function initQuotePopup() {
	initTestimonialQuoteClamps();
	initIndustryQuoteClamps();

	const modal = document.querySelector('.js-quote-modal');
	if (!modal) {
		return;
	}

	const textEl = modal.querySelector('.js-quote-modal-text');
	const authorEl = modal.querySelector('.js-quote-modal-author');
	let lastTrigger = null;
	let savedScrollY = 0;

	const open = (button) => {
		// If the exit-intent popup happens to be open (its 0.6 backdrop would
		// otherwise show behind this dialog instead of the page), close it first
		// so the two never stack. Try its own close control, then hard-hide as a
		// fallback (its module may not have a live listener).
		const exitPopup = document.querySelector('.js-exit-popup');
		if (exitPopup && !exitPopup.hidden) {
			exitPopup.querySelector('.js-exit-popup-close')?.click();
			if (!exitPopup.hidden) {
				exitPopup.hidden = true;
				exitPopup.setAttribute('aria-hidden', 'true');
				document.body.classList.remove('is-exit-popup-open');
			}
		}

		const quote = button.dataset.quote ?? '';
		const author = button.dataset.quoteAuthor ?? '';
		const role = button.dataset.quoteRole ?? '';

		if (textEl) {
			textEl.textContent = quote;
		}
		if (authorEl) {
			const parts = [author, role].filter(Boolean);
			authorEl.textContent = parts.join(' — ');
			authorEl.hidden = parts.length === 0;
		}

		lastTrigger = button;
		modal.hidden = false;
		modal.setAttribute('aria-hidden', 'false');
		// Position-preserving scroll lock. `body { overflow: hidden }` alone
		// collapses the page scroll to y=0 on open (the page jumps to the top,
		// and on mobile Safari it can stay there after close — #112). Instead,
		// pin the body at its current offset so the page stays visually put;
		// the scroll position is restored on close.
		savedScrollY = window.scrollY;
		// Locking removes the scrollbar, which widens the viewport by its own
		// width and shifts all content sideways. Pad the body by that same
		// amount so nothing visibly jumps when the modal opens/closes.
		const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
		if (scrollbarWidth > 0) {
			document.body.style.paddingRight = `${scrollbarWidth}px`;
		}
		document.body.classList.add('is-quote-modal-open');
		document.body.style.top = `-${savedScrollY}px`;
		// preventScroll: the modal is fixed/full-viewport already; without
		// this, some browsers compute the close button's position before the
		// unhide reflow finishes and jump the page scroll to y=0.
		modal.querySelector('.js-quote-modal-close')?.focus({ preventScroll: true });
	};

	const close = () => {
		modal.hidden = true;
		modal.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('is-quote-modal-open');
		document.body.style.top = '';
		document.body.style.paddingRight = '';
		// Restore the scroll position the body pin was holding.
		window.scrollTo(0, savedScrollY);
		lastTrigger?.focus({ preventScroll: true });
		lastTrigger = null;
	};

	document.addEventListener('click', (event) => {
		const trigger = event.target.closest('.js-quote-more');
		if (trigger) {
			event.preventDefault();
			open(trigger);
			return;
		}
		if (event.target.closest('.js-quote-modal-close')) {
			close();
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && !modal.hidden) {
			close();
		}
	});

	// iOS: while the modal is open the body is pinned (position: fixed), so the
	// document sits at scrollTop 0. A drag on the backdrop — or on a dialog
	// whose text doesn't overflow — rubber-bands the document at "top", which
	// arms Safari's pull-to-refresh; it fires when the finger lifts (typically
	// on the close tap) and reloads the page. The CSS overscroll-behavior
	// containment covers scrollable areas on iOS 16+; this guard covers the
	// backdrop (not a scroll container) and older iOS.
	modal.addEventListener(
		'touchmove',
		(event) => {
			const dialog = event.target.closest('.quote-modal__dialog');
			if (!dialog || dialog.scrollHeight <= dialog.clientHeight) {
				event.preventDefault();
			}
		},
		{ passive: false },
	);
}

/**
 * Inline 2-line clamp with a Mehr anzeigen / Weniger anzeigen toggle for the
 * case-study cards. Independent of the quote modal so it works on any page.
 * (The cs-testimonials reviews used to share this; they open the modal now — a
 * click inside a Swiper is unreliable, and it matches the home page (#176).)
 */
export function initQuoteClamp() {
	const clamps = document.querySelectorAll('.js-quote-clamp');
	if (!clamps.length) {
		return;
	}

	const refresh = (clamp) => {
		const toggle = clamp.querySelector('.js-quote-clamp-toggle');
		const text = clamp.querySelector('.case-studies__quote-text');
		if (!toggle || !text) {
			return;
		}

		// Only offer the toggle when the clamped text actually overflows.
		if (!clamp.classList.contains('is-expanded')) {
			toggle.hidden = text.scrollHeight - text.clientHeight <= 1;
		}
	};

	const refreshAll = () => clamps.forEach(refresh);

	refreshAll();
	// Re-measure once webfonts are in: before that the text is laid out with a
	// fallback font and can look like it overflows when it doesn't.
	if (document.fonts?.ready) {
		document.fonts.ready.then(refreshAll);
	}

	document.addEventListener('click', (event) => {
		const toggle = event.target.closest('.js-quote-clamp-toggle');
		if (!toggle) {
			return;
		}

		event.preventDefault();
		const clamp = toggle.closest('.js-quote-clamp');
		const expanded = clamp?.classList.toggle('is-expanded');
		toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
		toggle.textContent = expanded ? 'Weniger anzeigen' : 'Mehr anzeigen';
	});

	let resizeTimer = null;
	window.addEventListener('resize', () => {
		window.clearTimeout(resizeTimer);
		resizeTimer = window.setTimeout(() => {
			clamps.forEach((clamp) => {
				if (!clamp.classList.contains('is-expanded')) {
					refresh(clamp);
				}
			});
		}, 150);
	});
}

/**
 * Case-study grid cards: the template renders only the first sentence of the
 * quote plus a "Mehr anzeigen" button, with the full text in
 * `data-quote-full`. Swap the two in place on click.
 */
export function initQuoteExpand() {
	document.addEventListener('click', (event) => {
		const toggle = event.target.closest('.js-quote-expand');
		if (!toggle) {
			return;
		}

		const text = toggle.closest('.cs-grid-card__quote')?.querySelector('.js-quote-expandable');
		if (!text) {
			return;
		}

		event.preventDefault();

		// Remember the truncated version the first time we expand.
		if (text.dataset.quoteShort === undefined) {
			text.dataset.quoteShort = text.textContent;
		}

		const expanded = toggle.getAttribute('aria-expanded') !== 'true';
		text.textContent = expanded ? (text.dataset.quoteFull ?? '') : text.dataset.quoteShort;
		toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
		toggle.textContent = expanded ? 'Weniger anzeigen' : 'Mehr anzeigen';
	});
}
